import { GeoLocationProvider } from "@/contexts/GeoLocation"
import { RealtimeProvider } from "@/contexts/Realtime"
import { SettingsProvider } from "@/contexts/settings"
import { TrackRecordingController } from "@/components/app/TrackRecordingController"
import { getActiveProject, setActiveProject } from "@/hooks/useActiveProject"
import { useOrgLocationsStream } from "@/hooks/useLatestLocations"
import { useProjectsListStream } from "@/hooks/useProjectsList"
import { usePushRegistration } from "@/hooks/usePushRegistration"
import { getSessionWithOfflineFallback } from "@/lib/offlineAuth"
import { queryClient } from "@/lib/queryClient"
import { Spinner } from "@fieldbook/ui/spinner"
import { Outlet, createFileRoute, redirect } from "@tanstack/react-router"
import { Suspense } from "react"

function AuthLayout() {
  usePushRegistration()
  useProjectsListStream()
  useOrgLocationsStream()
  return (
    <GeoLocationProvider>
      <SettingsProvider>
        <TrackRecordingController />
        <RealtimeProvider>
          <Outlet />
        </RealtimeProvider>
      </SettingsProvider>
    </GeoLocationProvider>
  )
}

export const Route = createFileRoute("/_private")({
  beforeLoad: async ({ context, location }) => {
    // If context says logged in, trust it
    if (context.isLoggedIn) return

    // Only hit Supabase when context says not logged in
    const { session } = await getSessionWithOfflineFallback()
    if (!session) {
      // prevent ugly redirect loop if an active project is found
      if (getActiveProject()) setActiveProject(null)
      throw redirect({ to: "/auth/login", search: { redirect: location.href } })
    }
    // Seed the session query so useAuth derives user/org/role immediately
    // without waiting for a refetch. app_metadata on the session carries
    // org_id / org_name / role from the custom access-token hook.
    queryClient.setQueryData(["auth", "session"], session)
    return { isLoggedIn: true }
  },
  component: () => (
    <Suspense
      fallback={
        <div className="px-auto mx-auto h-screen">
          <Spinner size={"large"} />
        </div>
      }
    >
      <AuthLayout />
    </Suspense>
  ),
})
